import _ from 'lodash';

import { request } from '@/utils/infrastructure/communication';
import {
  getCurrentSession,
  getSessionItem,
  SESSION_TYPES,
  STORAGE_KEYS,
} from '@/utils/infrastructure/auth';
import { parseJson } from '../utils/infrastructure/utils';

const controller = new AbortController();
const { signal } = controller;

const getPortalConfig = () => {
  const value = getSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
  if (value) {
    try {
      return JSON.parse(value);
    } catch (e) {}
  }
  return false;
};

const getAppContextId = () => {
  try {
    const session = getCurrentSession();
    if (session?.type === SESSION_TYPES.APP && session.resourceId) {
      return session.resourceId;
    }
  } catch (e) {}
  return null;
};

export async function authLogin({ route, headers, ...params }) {
  return request(route || '/auth', {
    method: 'POST',
    credentials: 'include',
    data: params,
    headers,
    signal,
  });
}

export async function authPartyLogin({ headers, isSsoFlow, ssoClientId, ...params }) {
  return request(`/auth/${params.username}`, {
    method: 'POST',
    credentials: 'include',
    data: {
      ...params,
      ...(isSsoFlow && { isSsoFlow: true }),
      ...(isSsoFlow && ssoClientId && { ssoClientId }),
    },
    ...(headers && { headers }),
    signal,
  });
}

export async function sendTwoFactorAuthCode(data) {
  const portal = getPortalConfig();
  return request('/auth/sendTwoFactorAuthCode', {
    method: 'POST',
    data,
    ...(portal && { headers: { portalId: portal.id } }),
    signal,
  });
}

export async function validateSecurityCode(data) {
  const portal = getPortalConfig();
  return request('/auth/validateCode', {
    method: 'POST',
    data,
    ...(portal && { headers: { portalId: portal.id } }),
    signal,
  });
}

/**
 * Refreshes an access token.
 *
 * @param {string|Object} token - Current token (string or parsed object)
 * @param {{ appId?: string|null, portal?: Object|null }|null} contextOverride -
 *
 *   When provided, uses these values instead of reading from the current active session.
 *   Required when the session being refreshed differs from the tab's active session
 *   (e.g. an APP token refresh triggered while AUTOMY is the active session).
 */
export async function authRefreshToken(token, contextOverride = null) {
  const { accessToken, refreshToken } = parseJson(token);

  let appId, portal;
  if (contextOverride) {
    appId = contextOverride.appId ?? null;
    portal = contextOverride.portal ?? null;
  } else {
    appId = getAppContextId();
    portal = !appId ? getPortalConfig() : null;
  }

  const headers = {};

  if (appId) headers.appId = appId;
  else if (portal) headers.portalId = portal.id;

  return request('/auth/refreshToken', {
    method: 'POST',
    data: { accessToken, refreshToken },
    ...(Object.keys(headers).length > 0 && { headers }),
    signal,
  });
}

export async function accountConfirm({ token }) {
  return request('/auth/confirmAccount', {
    method: 'POST',
    data: { token },
    signal,
  });
}

export async function authLogout() {
  return request('/auth/logout', {
    method: 'POST',
    signal,
  });
}

export async function validateSsoClient({ clientId, redirectUrl }) {
  return request('/auth/sso/validateClient', {
    method: 'POST',
    data: { clientId, redirectUrl },
    signal,
  });
}
